import { Card, Table, Button, Row, Col } from 'react-bootstrap'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'

// 註冊 Chart.js 組件
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const BloodPressureRecord = ({
  data,
  startDate,
  endDate,
  setShowAddForm,
  onAddRecord,
}) => {
  // 處理圖表數據
  const processChartData = () => {
    if (!startDate || !endDate || !Array.isArray(data))
      return {
        labels: [],
        datasets: [{ data: [] }],
      }

    // 生成日期範圍內的所有日期
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }

    // 轉換為數組並排序
    const allDates = Array.from(dateSet).sort()

    // 按日期分組並計算平均值
    const dailyData = allDates.map((date) => {
      const dayRecords = data.filter((record) => {
        return (
          record &&
          record.record_time &&
          typeof record.record_time === 'string' &&
          record.record_time.startsWith(date)
        )
      })

      if (dayRecords.length === 0) {
        return {
          date,
          record_time: `${date} 00:00:00`,
          sbp: null,
          dbp: null,
        }
      }

      // 計算當日平均值
      const avgSbp = Math.round(
        dayRecords.reduce((sum, record) => sum + (record.sbp || 0), 0) /
          dayRecords.length
      )
      const avgDbp = Math.round(
        dayRecords.reduce((sum, record) => sum + (record.dbp || 0), 0) /
          dayRecords.length
      )

      return {
        date,
        record_time: dayRecords[dayRecords.length - 1].record_time,
        sbp: avgSbp,
        dbp: avgDbp,
      }
    })

    // 提取日期和時間
    const formatDateTime = (dateTimeStr) => {
      const date = new Date(dateTimeStr)
      const month = date.getMonth() + 1
      const day = date.getDate()
      return `${month}/${day}`
    }

    return {
      labels: allDates.map((date) => formatDateTime(`${date} 00:00:00`)),
      datasets: [
        {
          label: '血壓範圍',
          data: dailyData.map((item) => {
            const formattedDate = formatDateTime(item.record_time)
            return item.sbp === null
              ? { x: formattedDate, y: [0, 0] } // 使用 [0, 0] 替代 null
              : { x: formattedDate, y: [item.dbp, item.sbp] }
          }),
          backgroundColor: dailyData.map((item) =>
            item.sbp === null
              ? 'rgba(0, 0, 0, 0)' // 完全透明
              : item.sbp >= 130 || item.dbp >= 85
                ? 'rgba(255, 99, 132, 0.7)'
                : 'rgba(75, 192, 192, 0.7)'
          ),
          borderColor: dailyData.map((item) =>
            item.sbp === null
              ? 'rgba(0, 0, 0, 0)'
              : item.sbp >= 130 || item.dbp >= 85
                ? 'rgb(255, 99, 132)'
                : 'rgb(75, 192, 192)'
          ),
          borderWidth: 0,
          borderRadius: 8, // 增加圓角
          borderSkipped: false,
          barPercentage: 0.3, // 調整柱狀圖的寬度
        },
      ],
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const values = context.raw.y
            // 檢查是否為空值的特殊情況
            if (values[0] === 0 && values[1] === 0) {
              return '無數據'
            }
            return `收縮壓: ${values[1]} mmHg, 舒張壓: ${values[0]} mmHg`
          },
        },
      },
    },
    scales: {
      y: {
        grid: {
          color: '#CCCCCC',
          borderDash: [2, 2],
        },
        ticks: {
          stepSize: 50,
        },
        title: {
          display: true,
          text: 'mmHg',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  }

  // =================================== 紀錄表格用

  // 組織數據按日期分組
  const groupDataByDate = (records) => {
    if (!startDate || !endDate || !Array.isArray(records)) return []

    // 生成日期範圍內的所有日期
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }

    // 轉換為數組並排序（倒序，最新的在前）
    let allDates = Array.from(dateSet).sort((a, b) => new Date(b) - new Date(a))

    // 創建日期到記錄的映射
    const recordsByDate = records.reduce((acc, record) => {
      if (!record || !record.record_time) return acc
      const date = record.record_time.split(' ')[0]
      if (!acc[date]) acc[date] = []
      acc[date].push(record)
      return acc
    }, {})

    // 處理每一天的數據
    return allDates.map((date) => {
      const dayRecords = recordsByDate[date] || []

      // 分早晚
      const morningRecords = dayRecords.filter((record) => {
        if (!record || !record.record_time) return false
        const hour = new Date(record.record_time).getHours()
        return hour < 12
      })

      const eveningRecords = dayRecords.filter((record) => {
        if (!record || !record.record_time) return false
        const hour = new Date(record.record_time).getHours()
        return hour >= 12
      })

      // 如果沒有記錄，添加空記錄
      if (morningRecords.length === 0) {
        morningRecords.push({
          sbp: '-',
          dbp: '-',
          comment: '-',
          record_time: `${date} 00:00:00`,
        })
      }

      if (eveningRecords.length === 0) {
        eveningRecords.push({
          sbp: '-',
          dbp: '-',
          comment: '-',
          record_time: `${date} 00:00:00`,
        })
      }

      return {
        date,
        morning: morningRecords,
        evening: eveningRecords,
      }
    })
  }

  // 格式化時間
  const formatTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr)
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  }

  // 格式化日期
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return `${dateStr} ${weekdays[date.getDay()]}`
  }

  // const hasData = data && data.length > 0

  // const renderNoData = () => (
  //   <div className="text-center py-5 text-muted">
  //     <i className="fa-solid fa-database mb-3 fa-2x"></i>
  //     <p className="mb-0">目前無數據記錄</p>
  //   </div>
  // )

  const groupedData = groupDataByDate(data || [])

  return (
    <Row className="blood-pressure-record g-3">
      {/* Chart Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <Card.Title className="d-flex justify-content-end align-items-center mb-4">
              {/* <span>血壓趨勢圖</span> */}
              <div className="ms-3 d-flex">
                <span className="me-3">
                  <i
                    className="fas fa-square me-1"
                    style={{ color: 'rgba(75, 192, 192, 0.7)' }}
                  ></i>
                  正常
                </span>
                <span>
                  <i
                    className="fas fa-square me-1"
                    style={{ color: 'rgba(255, 99, 132, 0.7)' }}
                  ></i>
                  偏高
                </span>
              </div>
            </Card.Title>
            <div className="responsive-chart">
              <Bar
                key={JSON.stringify(data)}
                style={{ height: '400px' }}
                options={chartOptions}
                data={processChartData()}
              />
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Table Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <div className="table-responsive">
              {groupedData.map((dayGroup, index) => (
                <Table
                  key={dayGroup.date}
                  hover
                  className="mb-0 border rounded-3 overflow-hidden"
                >
                  <thead>
                    <tr className="table-light">
                      <th colSpan="3">{formatDate(dayGroup.date)}</th>
                      <th className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="px-3"
                          onClick={() => {
                            setShowAddForm(true)
                            onAddRecord?.(dayGroup.date)
                          }}
                        >
                          <i className="fa-solid fa-plus me-2"></i>
                          新增
                        </Button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayGroup.morning.length === 1 &&
                    dayGroup.morning[0].sbp === '-' &&
                    dayGroup.evening.length === 1 &&
                    dayGroup.evening[0].sbp === '-' ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          尚無血壓紀錄
                        </td>
                      </tr>
                    ) : (
                      <>
                        {/* 早上的記錄 */}
                        {dayGroup.morning.map((record, recordIndex) => (
                          <tr key={`morning-${recordIndex}`}>
                            {recordIndex === 0 && (
                              <td rowSpan={dayGroup.morning.length}>早</td>
                            )}
                            <td>{formatTime(record.record_time)}</td>
                            <td>
                              {record.sbp === '-' ? (
                                '- / - mmHg'
                              ) : (
                                <span>
                                  <span
                                    className={
                                      record.sbp >= 130 ? 'text-danger' : ''
                                    }
                                  >
                                    {record.sbp}
                                  </span>
                                  {' / '}
                                  <span
                                    className={
                                      record.dbp >= 85 ? 'text-danger' : ''
                                    }
                                  >
                                    {record.dbp}
                                  </span>
                                  {' mmHg'}
                                </span>
                              )}
                            </td>
                            <td>{record.comment || '-'}</td>
                          </tr>
                        ))}

                        {/* 晚上的記錄 */}
                        {dayGroup.evening.map((record, recordIndex) => (
                          <tr key={`evening-${recordIndex}`}>
                            {recordIndex === 0 && (
                              <td rowSpan={dayGroup.evening.length}>晚</td>
                            )}
                            <td>{formatTime(record.record_time)}</td>
                            <td>
                              {record.sbp === '-' ? (
                                '- / - mmHg'
                              ) : (
                                <span>
                                  <span
                                    className={
                                      record.sbp >= 130 ? 'text-danger' : ''
                                    }
                                  >
                                    {record.sbp}
                                  </span>
                                  {' / '}
                                  <span
                                    className={
                                      record.dbp >= 85 ? 'text-danger' : ''
                                    }
                                  >
                                    {record.dbp}
                                  </span>
                                  {' mmHg'}
                                </span>
                              )}
                            </td>
                            <td>{record.comment || '-'}</td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </Table>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default BloodPressureRecord
